import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import PasswordField from '../components/shell/PasswordField';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { GraduationCap, Buildings, CheckCircle } from '@phosphor-icons/react';
import { toast } from 'sonner';
import axios from 'axios';
import { getPlatformDomains, getCachedBaseDomains } from '../lib/tenantDetection';

import { API_URL } from '../config/api';
import { BRAND_NAME } from '../brand';

export default function TenantRegisterPage() {
  const [baseDomain, setBaseDomain] = useState(getCachedBaseDomains().default_base_domain);
  useEffect(() => { getPlatformDomains().then((p) => setBaseDomain(p.default_base_domain)); }, []);
  const [formData, setFormData] = useState({
    organization_name: '',
    subdomain: '',
    admin_email: '',
    admin_password: '',
    admin_first_name: '',
    admin_last_name: ''
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(null);
  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value } = e.target;
    // For subdomain, convert to lowercase and remove special characters
    if (name === 'subdomain') {
      const sanitized = value.toLowerCase().replace(/[^a-z0-9-]/g, '');
      setFormData(prev => ({ ...prev, [name]: sanitized }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API_URL}/public/register-tenant`, formData);
      setSuccess(response.data);
      toast.success('Organization registered successfully!');
    } catch (error) {
      const message = error.response?.data?.detail || 'Registration failed. Please try again.';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center p-8 bg-gradient-to-br from-primary/5 via-background to-primary/10">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mb-4">
              <CheckCircle className="w-10 h-10 text-green-600" weight="fill" />
            </div>
            <CardTitle className="font-heading text-2xl text-green-700">Registration Successful!</CardTitle>
            <CardDescription>Your organization has been created</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-muted rounded-lg p-4 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Organization:</span>
                <span className="font-medium">{success.subdomain}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Admin Email:</span>
                <span className="font-medium">{success.admin_email}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Portal URL:</span>
                <span className="font-medium text-primary">/tenant/{success.subdomain}</span>
              </div>
            </div>

            <div className="pt-4 space-y-3">
              <Button 
                className="w-full" 
                onClick={() => navigate(`/tenant/${success.subdomain}/login`)}
                data-testid="go-to-portal-btn"
              >
                Go to Your Portal
              </Button>
              <Button 
                variant="outline" 
                className="w-full" 
                onClick={() => navigate('/login')}
                data-testid="main-login-btn"
              >
                Go to Main Login
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left side - Form */}
      <div className="flex items-center justify-center p-8 bg-background overflow-y-auto">
        <div className="w-full max-w-md space-y-6">
          <div className="flex items-center gap-3">
            <Link to="/" className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center">
                <GraduationCap className="w-6 h-6 text-primary-foreground" weight="duotone" />
              </div>
              <span className="font-heading font-bold text-xl">{BRAND_NAME}</span>
            </Link>
          </div>

          <Card className="border-0 shadow-none bg-transparent">
            <CardHeader className="px-0 pb-4">
              <div className="flex items-center gap-2 mb-2">
                <Buildings className="w-6 h-6 text-primary" weight="duotone" />
                <span className="text-sm font-medium text-primary">For Organizations</span>
              </div>
              <CardTitle className="font-heading text-2xl">Register Your Organization</CardTitle>
              <CardDescription>Create your own learning portal with custom subdomain</CardDescription>
            </CardHeader>
            <CardContent className="px-0">
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Organization Details */}
                <div className="space-y-4 pb-4 border-b">
                  <h3 className="text-sm font-medium text-muted-foreground">Organization Details</h3>
                  
                  <div className="field">
                    <Label htmlFor="organization_name">Organization Name</Label>
                    <Input
                      id="organization_name"
                      name="organization_name"
                      type="text"
                      placeholder="Acme Corporation"
                      value={formData.organization_name}
                      onChange={handleChange}
                      required
                      data-testid="org-name-input"
                    />
                  </div>

                  <div className="field">
                    <Label htmlFor="subdomain">Subdomain</Label>
                    <div className="flex items-center gap-2">
                      <Input
                        id="subdomain"
                        name="subdomain"
                        type="text"
                        placeholder="acme"
                        value={formData.subdomain}
                        onChange={handleChange}
                        required
                        minLength={3}
                        maxLength={30}
                        pattern="^[a-z0-9-]+$"
                        data-testid="subdomain-input"
                      />
                      <span className="text-sm text-muted-foreground whitespace-nowrap">.{baseDomain}</span>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Only lowercase letters, numbers, and hyphens. 3-30 characters.
                    </p>
                  </div>
                </div>

                {/* Admin Account */}
                <div className="space-y-4 pt-2">
                  <h3 className="text-sm font-medium text-muted-foreground">Admin Account</h3>
                  
                  <div className="grid grid-cols-2 gap-3">
                    <div className="field">
                      <Label htmlFor="admin_first_name">First Name</Label>
                      <Input
                        id="admin_first_name"
                        name="admin_first_name"
                        type="text"
                        placeholder="John"
                        value={formData.admin_first_name}
                        onChange={handleChange}
                        required
                        data-testid="first-name-input"
                      />
                    </div>
                    <div className="field">
                      <Label htmlFor="admin_last_name">Last Name</Label>
                      <Input
                        id="admin_last_name"
                        name="admin_last_name"
                        type="text"
                        placeholder="Doe"
                        value={formData.admin_last_name}
                        onChange={handleChange}
                        required
                        data-testid="last-name-input"
                      />
                    </div>
                  </div>

                  <div className="field">
                    <Label htmlFor="admin_email">Admin Email</Label>
                    <Input
                      id="admin_email"
                      name="admin_email"
                      type="email"
                      placeholder="admin@company.com"
                      value={formData.admin_email}
                      onChange={handleChange}
                      required
                      data-testid="email-input"
                    />
                  </div>

                  <PasswordField
  id="admin_password"
  label="Password"
  value={formData.admin_password}
  onChange={(v) => handleChange({ target: { name: 'admin_password', value: v } })}
  placeholder="Min 6 characters"
  required
  minLength={6}
  testid="password-input"
  generate
/>
                </div>

                <Button type="submit" className="w-full" disabled={loading} data-testid="register-submit">
                  {loading ? 'Creating Organization...' : 'Create Organization'}
                </Button>

                <p className="text-center text-sm text-muted-foreground">
                  Already have an account?{' '}
                  <Link to="/login" className="text-primary hover:underline" data-testid="login-link">
                    Sign in
                  </Link>
                </p>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Right side - Image/Info */}
      <div className="hidden lg:block relative bg-gradient-to-br from-primary to-primary/80">
        <div className="absolute inset-0 bg-grid-white/10" />
        <div className="absolute inset-0 flex flex-col justify-center p-12 text-white">
          <h2 className="font-heading text-4xl font-bold mb-6">
            Launch Your Corporate Training Platform
          </h2>
          <ul className="space-y-4 text-lg opacity-90">
            <li className="flex items-start gap-3">
              <CheckCircle className="w-6 h-6 mt-0.5 flex-shrink-0" weight="fill" />
              <span>Custom branded portal with your subdomain</span>
            </li>
            <li className="flex items-start gap-3">
              <CheckCircle className="w-6 h-6 mt-0.5 flex-shrink-0" weight="fill" />
              <span>Unlimited courses and assessments</span>
            </li>
            <li className="flex items-start gap-3">
              <CheckCircle className="w-6 h-6 mt-0.5 flex-shrink-0" weight="fill" />
              <span>Invite instructors and manage learners</span>
            </li>
            <li className="flex items-start gap-3">
              <CheckCircle className="w-6 h-6 mt-0.5 flex-shrink-0" weight="fill" />
              <span>Analytics and reporting dashboard</span>
            </li>
            <li className="flex items-start gap-3">
              <CheckCircle className="w-6 h-6 mt-0.5 flex-shrink-0" weight="fill" />
              <span>Certificate generation and tracking</span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
}
